import { Ellipsis, History, Pencil, Plus, Trash } from 'lucide-react'

import { Money, StatusBadge } from '@/components/ds'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { CatalogProduct, CatalogSku, StockSummary, variantLabel } from '@/lib/catalog/catalogTypes'
import { cn } from '@/lib/utils'
import { formatQuantity, LOW_STOCK_THRESHOLD } from '@/lib/warehouse/stockLevels'

import { PriceEditPopover } from './PriceEditPopover'

interface ProductVariantsProps {
  product: CatalogProduct
  /** Varianty k zobrazení (vlastní SKU, případně jen systémové) */
  skus: CatalogSku[]
  /** Systémové SKU, přes které jde nastavit cena všem variantám najednou */
  systemSku?: CatalogSku
  stockBySkuId: Map<string, StockSummary> | null
  onChanged: () => void
  onAdd: () => void
  onEdit: (sku: CatalogSku) => void
  onDelete: (sku: CatalogSku) => void
  onShowHistory: (skuId: string) => void
}

const StockValue = ({ stock }: { stock?: StockSummary }) => {
  if (!stock) return <span className="text-muted-foreground">-</span>
  if (!stock.tracked) return <span className="text-muted-foreground">Neomezeno</span>
  const out = stock.available <= 0
  const low = !out && stock.available <= LOW_STOCK_THRESHOLD
  return (
    <span className="inline-flex items-center justify-end gap-2">
      {out && <StatusBadge status="overdue" label="Vyprodáno" />}
      {low && <StatusBadge status="pending" label="Dochází" />}
      <span className={cn('font-medium', out && 'text-danger')}>{formatQuantity(stock.available)}</span>
    </span>
  )
}

/** Krátký stav skladu do podřádku varianty na telefonu. */
const MobileStock = ({ stock }: { stock?: StockSummary }) => {
  if (!stock || !stock.tracked) return null
  const out = stock.available <= 0
  const low = !out && stock.available <= LOW_STOCK_THRESHOLD
  return (
    <span className={cn(out && 'text-danger', low && 'text-warning')}>
      {out ? 'vyprodáno' : `${formatQuantity(stock.available)} ks`}
    </span>
  )
}

/** Tabulka variant: název, kód SKU, cena (změna v popoveru), dostupné množství a součet skladem. */
export const ProductVariants = ({
  product,
  skus,
  systemSku,
  stockBySkuId,
  onChanged,
  onAdd,
  onEdit,
  onDelete,
  onShowHistory,
}: ProductVariantsProps) => {
  const axes = [...(product.variantAxes ?? [])].sort((a, b) => a.sortOrder - b.sortOrder)
  const showStock = stockBySkuId !== null
  // Varianty v pořadí hodnot os (250 g před 1 kg), ne v pořadí založení
  const valueOrder = new Map<string, number>()
  axes.forEach((a, i) => a.axis.values.forEach((v) => valueOrder.set(v.id, i * 1000 + v.sortOrder)))
  const sortKey = (sku: CatalogSku) => (sku.options ?? []).map((o) => valueOrder.get(o.valueId) ?? 999999)
  const sortedSkus = [...skus].sort((a, b) => {
    const ka = sortKey(a)
    const kb = sortKey(b)
    for (let i = 0; i < Math.max(ka.length, kb.length); i++) {
      const diff = (ka[i] ?? -1) - (kb[i] ?? -1)
      if (diff) return diff
    }
    return 0
  })
  const total = skus.reduce(
    (acc, sku) => {
      const stock = stockBySkuId?.get(sku.id)
      if (!stock) return acc
      if (stock.tracked) {
        acc.available += stock.available
        acc.onHand += stock.onHand
        acc.tracked = true
      }
      if (stock.unlimited && !stock.tracked) acc.unlimited = true
      return acc
    },
    { available: 0, onHand: 0, tracked: false, unlimited: false },
  )
  const hasUserSkus = skus.some((sku) => !sku.isSystem)
  const totalValue = total.tracked ? (
    <span className="font-medium">{formatQuantity(total.available)} ks</span>
  ) : (
    <span className="text-muted-foreground">{total.unlimited ? 'Neomezeno' : '-'}</span>
  )

  return (
    <section className="overflow-hidden rounded-lg border">
      <div className="flex flex-wrap items-start justify-between gap-3 px-5 pt-4 pb-3">
        <div className="min-w-0">
          <h2 className="text-sm font-medium">Varianty</h2>
          {axes.length > 0 ? (
            <p className="mt-0.5 text-xs text-muted-foreground">
              {axes.map((a) => `${a.axis.name}: ${a.axis.values.map((v) => v.label).join(', ')}`).join('; ')}
            </p>
          ) : (
            !hasUserSkus && <p className="mt-0.5 text-xs text-muted-foreground">Produkt se prodává v jedné variantě.</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {hasUserSkus && skus.length > 1 && systemSku && (
            <PriceEditPopover
              sku={systemSku}
              title="Cena pro všechny varianty"
              description="Přepíše cenu u všech variant produktu."
              onSaved={onChanged}
            >
              <Button variant="ghost" size="sm">
                Nastavit cenu všem
              </Button>
            </PriceEditPopover>
          )}
          <Button variant="outline" size="sm" onClick={onAdd}>
            <Plus />
            Přidat variantu
          </Button>
        </div>
      </div>

      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="pl-5">Varianta</TableHead>
            <TableHead className="hidden md:table-cell">Kód SKU</TableHead>
            <TableHead className="text-right md:w-36">Cena</TableHead>
            {showStock && <TableHead className="hidden w-40 text-right md:table-cell">Dostupné</TableHead>}
            <TableHead className="w-px" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedSkus.map((sku) => {
            const label = variantLabel(sku, product.name)
            return (
              <TableRow key={sku.id} className="group hover:bg-transparent">
                <TableCell className="max-w-0 w-full pl-5">
                  <div className="flex items-center gap-2 md:min-w-40">
                    <span className="truncate font-medium">{label}</span>
                    {!sku.isActive && <StatusBadge status="draft" label="Neaktivní" className="hidden md:inline-flex" />}
                  </div>
                  {!sku.isSystem && label !== sku.name && (
                    <p className="truncate text-xs text-muted-foreground">{sku.name}</p>
                  )}
                  <p className="truncate text-xs text-muted-foreground md:hidden">
                    {[!sku.isActive ? 'Neaktivní' : null, sku.isSystem ? null : sku.code].filter(Boolean).join(', ')}
                    {showStock && stockBySkuId?.get(sku.id)?.tracked && (
                      <>
                        {!sku.isActive || !sku.isSystem ? ', ' : ''}
                        <MobileStock stock={stockBySkuId?.get(sku.id)} />
                      </>
                    )}
                  </p>
                </TableCell>
                <TableCell className="hidden max-w-56 md:table-cell">
                  <p className="truncate font-mono text-xs text-muted-foreground" title={sku.code}>
                    {sku.isSystem ? '-' : sku.code}
                  </p>
                </TableCell>
                <TableCell className="text-right">
                  <PriceEditPopover sku={sku} description={label} onSaved={onChanged}>
                    <button
                      type="button"
                      className="-mr-2 inline-flex items-center gap-1.5 rounded-md px-2 py-1 transition-colors hover:bg-muted"
                    >
                      {sku.price?.price ? (
                        <Money amount={sku.price.price.amount} currency={sku.price.price.currency} />
                      ) : (
                        <span className="text-muted-foreground">Nastavit cenu</span>
                      )}
                      <Pencil className="size-3 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
                    </button>
                  </PriceEditPopover>
                </TableCell>
                {showStock && (
                  <TableCell className="hidden text-right md:table-cell">
                    <StockValue stock={stockBySkuId?.get(sku.id)} />
                  </TableCell>
                )}
                <TableCell className="w-px pl-0 pr-3 text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon-sm" aria-label="Akce varianty">
                        <Ellipsis />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => onShowHistory(sku.id)}>
                        <History />
                        Historie cen
                      </DropdownMenuItem>
                      {!sku.isSystem && (
                        <>
                          <DropdownMenuItem onClick={() => onEdit(sku)}>
                            <Pencil />
                            Přejmenovat
                          </DropdownMenuItem>
                          <DropdownMenuItem variant="destructive" onClick={() => onDelete(sku)}>
                            <Trash />
                            Smazat variantu
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
        {showStock && (
          <TableFooter className="bg-muted">
            <TableRow className="hover:bg-transparent">
              <TableCell colSpan={3} className="hidden pl-5 text-muted-foreground md:table-cell">
                Celkem na skladě
              </TableCell>
              <TableCell className="pl-5 text-muted-foreground md:hidden">Celkem na skladě</TableCell>
              <TableCell colSpan={2} className="text-right md:hidden">
                {totalValue}
              </TableCell>
              <TableCell className="hidden text-right md:table-cell">
                {totalValue}
              </TableCell>
              <TableCell className="hidden md:table-cell" />
            </TableRow>
          </TableFooter>
        )}
      </Table>
    </section>
  )
}
